/* ET bucket keys — the screener's own rules (src/apps/screener/bar-bins.ts dailyBinMs / weeklyBinMs and mv-core
   anchoredBucketStart), so a live print lands in the bucket the screener will store it in (mv5-prod/05 §3):
   - TFs up to 1H: the epoch floor (every TF that divides 60 minutes; ET offsets are whole hours);
   - 4H: anchored at 04:00 ET of the ET date (of the previous date before 04:00), in 4 h steps;
   - D: midnight ET of the ET date; W: midnight ET of that week's Monday;
   - prints outside 04:00–20:00 ET belong to no bar (the screener ignores them).
   The UTC browser floor this replaces put D at 00:00Z and W on Thursdays (the Unix epoch week). */

const ET = 'America/New_York';
const HOUR = 3_600_000;
const DAY = 86_400_000;

const fmt = new Intl.DateTimeFormat('en-US', {
  timeZone: ET, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});
const offsets = new Map<number, number>();

/** ET wall clock minus UTC, in ms, at `ms` — constant within a UTC hour (US DST switches on the hour), so cached. */
export function etOffsetMs(ms: number): number {
  const h = Math.floor(ms / HOUR);
  let o = offsets.get(h);
  if (o === undefined) {
    const p = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
    for (const x of fmt.formatToParts(new Date(h * HOUR))) if (x.type in p) p[x.type as keyof typeof p] = Number(x.value);
    o = Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second) - h * HOUR;
    if (offsets.size > 50_000) offsets.clear();
    offsets.set(h, o);
  }
  return o;
}

/** The ET wall clock at `ms`, read as if it were UTC ms. */
const wallOf = (ms: number): number => ms + etOffsetMs(ms);

/** The instant whose ET wall clock reads `wall` (for midnight / 04:00 — never inside a DST switch at 02:00). */
function instantOf(wall: number): number {
  const guess = wall - etOffsetMs(wall);
  return wall - etOffsetMs(guess);
}

/** Minute of the ET day at `ms` (0 = midnight ET). */
export function etMinuteOfDay(ms: number): number {
  const w = wallOf(ms);
  return Math.floor((((w % DAY) + DAY) % DAY) / 60_000);
}

/** Inside the 04:00–20:00 ET session the screener builds bars from. */
export function inSession(ms: number): boolean {
  const m = etMinuteOfDay(ms);
  return m >= 240 && m < 1200;
}

/** 04:00 ET of the session `ms` belongs to (of the previous ET date before 04:00) — where the day's bars start (the
 *  journal's day: journal-builder stages.ts `dayStart`). */
export function sessionStartMs(ms: number): number {
  const wallDay = Math.floor(wallOf(ms) / DAY) * DAY;
  const a = instantOf(wallDay + 4 * HOUR);
  return ms < a ? instantOf(wallDay - DAY + 4 * HOUR) : a;
}

/** Start (ms) of the bucket of a `tfMin`-minute timeframe that `ms` falls in — the screener's bin for that bar. */
export function bucketStartMs(ms: number, tfMin: number): number {
  if (tfMin <= 60) {
    const step = tfMin * 60_000;
    return Math.floor(ms / step) * step;
  }
  const wallDay = Math.floor(wallOf(ms) / DAY) * DAY;
  if (tfMin === 1440) return instantOf(wallDay);
  if (tfMin === 10_080) {
    const dow = new Date(wallDay).getUTCDay();           // 0 = Sunday … 6 = Saturday, of the ET date
    return instantOf(wallDay - ((dow + 6) % 7) * DAY);
  }
  const step = tfMin * 60_000;                            // 4H: anchored at 04:00 ET
  let a = instantOf(wallDay + 4 * HOUR);
  if (ms < a) a = instantOf(wallDay - DAY + 4 * HOUR);
  return a + Math.floor((ms - a) / step) * step;
}
